import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import StatsBanner from "@/components/StatsBanner";
import { GOV_PROGRAMS, PROJECTS } from "@/data/content";
import { ShieldCheck, FileBadge, Landmark, Workflow } from "lucide-react";

export default function GovernmentProjects() {
  const govProjects = PROJECTS.filter((p) =>
    ["DISCOM", "SECI", "TSSPDCL", "KSPDCL", "RVPN", "AP"].some((k) => p.client.includes(k))
  );

  return (
    <div data-testid="government-page">
      <PageHero
        overline="Government & Utility"
        title="Anchored to India's largest energy buyers."
        subtitle="Empanelled execution partner for PM-KUSUM, APDISCOM, Telangana DISCOM, SECI and multiple state nodal agencies."
        image="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
        testid="government-hero"
      />

      <section className="section-padding bg-white">
        <div className="container-corp">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6 mb-16">
            {[
              { icon: Landmark, title: "Central PSU Empanelment", body: "Trusted execution partner for MNRE-aligned tenders." },
              { icon: ShieldCheck, title: "DISCOM Approvals", body: "Pre-qualified vendor with multiple state utilities." },
              { icon: FileBadge, title: "Tender Track Record", body: "Strong technical & financial qualification credentials." },
              { icon: Workflow, title: "PPA Execution", body: "Long-term PPAs with central & state agencies." },
            ].map((b) => (
              <div key={b.title} className="card-corp" data-testid={`gov-feature-${b.title}`}>
                <b.icon className="text-[#16A34A] mb-4" size={28} strokeWidth={1.5} />
                <h3 className="font-heading text-base font-semibold text-[#0B2341] mb-2">{b.title}</h3>
                <p className="text-sm text-[#4A5568] leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#0B2341] text-white">
        <div className="container-corp">
          <div className="overline mb-4">Programs We Deliver</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold mb-14 max-w-2xl leading-tight">
            Programs and government partnerships.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {GOV_PROGRAMS.map((g, i) => (
              <div key={g.name} data-testid={`gov-program-${i}`} className="bg-[#0B2341] p-8 hover:bg-[#061426] transition-colors">
                <div className="font-mono text-xs text-[#16A34A] mb-4">/ {String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-heading text-xl font-medium mb-3">{g.name}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <StatsBanner />

      <section className="section-padding bg-[#F8F9FA]">
        <div className="container-corp">
          <div className="overline mb-4">Government Project References</div>
          <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-[#0B2341] mb-14 max-w-2xl leading-tight">
            A selection of public-sector executions.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(govProjects.length ? govProjects : PROJECTS.slice(0, 3)).map((p) => (
              <div key={p.slug} data-testid={`gov-project-${p.slug}`} className="bg-white border border-[#E2E8F0] hover:shadow-xl transition-all">
                <div className="relative aspect-[4/3]">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  <div className="absolute top-4 left-4 bg-[#16A34A] text-[#0B2341] text-xs font-bold px-3 py-1.5 uppercase tracking-wider">
                    {p.capacity}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-[#0B2341] mb-2">{p.name}</h3>
                  <div className="text-xs text-[#4A5568] mb-1">{p.location}</div>
                  <div className="text-xs text-[#16A34A] uppercase tracking-[0.18em] mt-2">{p.client}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        overline="DISCOM, PSU or Nodal Agency?"
        title="Partner with VPCL Infra on your next tender."
        body="Our tender response and EPC delivery teams are pre-qualified with multiple central and state agencies."
      />
    </div>
  );
}
